import React, { useState } from "react";
import { Line, Bar } from "react-chartjs-2";
import { motion } from "framer-motion";

import {
  FaChartLine,
  FaChartBar,
  FaChartPie,
  FaLightbulb,
  FaExchangeAlt,
  FaArrowUp,
  FaArrowDown,
  FaRocket,
  FaPauseCircle,
  FaTrophy,
  FaCheckCircle,
} from "react-icons/fa";

import ReusableModal from "../../components/ReusableModal";

/* ──────────────────────────────────────────────────────────── */
/*  Small reusable heading icon (same pattern as parent page)  */
/* ──────────────────────────────────────────────────────────── */
const HeadingIcon = ({ icon: Icon, color, bg }) => (
  <div
    style={{
      width: 32,
      height: 32,
      borderRadius: 10,
      background: bg || "rgba(111,66,193,0.15)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    <Icon size={16} color={color || "var(--color-primary)"} />
  </div>
);

/* ──────────────────────────────────────────────────────────── */
/*                  Computation helpers                        */
/* ──────────────────────────────────────────────────────────── */

const computeInsights = (trend) => {
  if (!trend || !trend.labels || !trend.endpoints) return [];
  const { labels, endpoints } = trend;
  const tools = Object.keys(endpoints);
  const lastIdx = labels.length - 1;
  if (lastIdx < 1) return [];
  const insights = [];

  let biggestGrower = null;
  let biggestDecliner = null;

  tools.forEach((tool) => {
    const vals = endpoints[tool];
    if (!vals || vals.length < 2) return;
    const curr = vals[lastIdx];
    const prev = vals[lastIdx - 1];
    const delta = curr - prev;
    const pct = prev > 0 ? ((delta / prev) * 100).toFixed(1) : "N/A";

    // Biggest grower
    if (delta > 0 && (!biggestGrower || delta > biggestGrower.delta)) {
      biggestGrower = { tool, delta, pct, curr };
    }
    // Biggest decliner
    if (delta < 0 && (!biggestDecliner || delta < biggestDecliner.delta)) {
      biggestDecliner = {
        tool,
        delta,
        pct: prev > 0 ? ((delta / prev) * 100).toFixed(1) : "N/A",
        curr,
      };
    }

    // Stagnant check — same value for 3+ consecutive months at the end
    let stagnantCount = 1;
    for (let i = lastIdx - 1; i >= 0; i--) {
      if (vals[i] === curr) stagnantCount++;
      else break;
    }
    if (stagnantCount >= 3) {
      insights.push({
        icon: FaPauseCircle,
        iconColor: "#d97706",
        text: `${tool} has not changed in ${stagnantCount} months (holding at ${curr})`,
        type: "stagnant",
        priority: 2,
      });
    }

    // All-time high
    const maxVal = Math.max(...vals);
    if (curr === maxVal && curr > 0 && delta > 0) {
      insights.push({
        icon: FaTrophy,
        iconColor: "#2563eb",
        text: `${tool} hit its highest count ever this month (${curr})`,
        type: "record",
        priority: 3,
      });
    }

    // Consecutive growth streak (3+)
    let growthStreak = 0;
    for (let i = lastIdx; i >= 1; i--) {
      if (vals[i] > vals[i - 1]) growthStreak++;
      else break;
    }
    if (growthStreak >= 3) {
      insights.push({
        icon: FaChartLine,
        iconColor: "#16a34a",
        text: `${tool} has grown for ${growthStreak} straight months`,
        type: "streak",
        priority: 4,
      });
    }

    // Consecutive decline streak (3+)
    let declineStreak = 0;
    for (let i = lastIdx; i >= 1; i--) {
      if (vals[i] < vals[i - 1]) declineStreak++;
      else break;
    }
    if (declineStreak >= 3) {
      insights.push({
        icon: FaChartLine,
        iconColor: "#dc2626",
        text: `${tool} has declined for ${declineStreak} straight months`,
        type: "decline",
        priority: 1,
      });
    }
  });

  // Add biggest grower/decliner as top insights
  if (biggestGrower) {
    insights.unshift({
      icon: FaRocket,
      iconColor: "#16a34a",
      text: `${biggestGrower.tool} grew the most this month: +${biggestGrower.delta} (${biggestGrower.pct !== "N/A" ? "+" + biggestGrower.pct + "%" : ""})`,
      type: "growth",
      priority: 0,
    });
  }
  if (biggestDecliner) {
    insights.splice(biggestGrower ? 1 : 0, 0, {
      icon: FaArrowDown,
      iconColor: "#dc2626",
      text: `${biggestDecliner.tool} dropped the most this month: ${biggestDecliner.delta} (${biggestDecliner.pct !== "N/A" ? biggestDecliner.pct + "%" : ""})`,
      type: "decline",
      priority: 0,
    });
  }

  // If no insights generated, add a neutral one
  if (insights.length === 0) {
    insights.push({
      icon: FaCheckCircle,
      iconColor: "#16a34a",
      text: "All tools are stable — no significant changes detected",
      type: "neutral",
      priority: 5,
    });
  }

  return insights.sort((a, b) => a.priority - b.priority).slice(0, 6);
};

const computeComparison = (trend, indexA, indexB) => {
  if (!trend || !trend.endpoints || indexA == null || indexB == null) return [];
  const { endpoints, colors } = trend;
  const tools = Object.keys(endpoints);
  return tools
    .map((tool) => {
      const vals = endpoints[tool];
      const valueA = vals[indexA] || 0;
      const valueB = vals[indexB] || 0;
      const delta = valueB - valueA;
      const pctChange =
        valueA > 0
          ? ((delta / valueA) * 100).toFixed(1)
          : valueB > 0
          ? "NEW"
          : "0.0";
      return {
        tool,
        valueA,
        valueB,
        delta,
        pctChange,
        color: colors?.[tool] || "#667eea",
      };
    })
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
};

const computeShare = (trend, monthIndex) => {
  if (!trend || !trend.endpoints) return [];
  const { endpoints, colors, labels } = trend;
  const tools = Object.keys(endpoints);
  const idx = monthIndex != null ? monthIndex : labels.length - 1;
  const total = tools.reduce(
    (sum, tool) => sum + (endpoints[tool]?.[idx] || 0),
    0
  );
  if (total === 0) return [];

  return tools
    .map((tool) => {
      const value = endpoints[tool]?.[idx] || 0;
      const share = ((value / total) * 100).toFixed(1);
      // Sparkline: last 6 months of share %
      const sparkData = [];
      for (let i = Math.max(0, idx - 5); i <= idx; i++) {
        const monthTotal = tools.reduce(
          (s, t) => s + (endpoints[t]?.[i] || 0),
          0
        );
        sparkData.push(
          monthTotal > 0
            ? ((endpoints[tool]?.[i] || 0) / monthTotal) * 100
            : 0
        );
      }
      return {
        tool,
        value,
        share: parseFloat(share),
        color: colors?.[tool] || "#667eea",
        sparkData,
      };
    })
    .filter((item) => item.value > 0)
    .sort((a, b) => b.share - a.share);
};

/* ──────────────────────────────────────────────────────────── */
/*                Insight color palette                        */
/* ──────────────────────────────────────────────────────────── */
const INSIGHT_COLORS = {
  growth: { bg: "rgba(34,197,94,0.08)", border: "#22c55e", text: "#16a34a" },
  decline: { bg: "rgba(239,68,68,0.08)", border: "#ef4444", text: "#dc2626" },
  stagnant: { bg: "rgba(245,158,11,0.08)", border: "#f59e0b", text: "#d97706" },
  record: { bg: "rgba(59,130,246,0.08)", border: "#3b82f6", text: "#2563eb" },
  streak: { bg: "rgba(99,102,241,0.08)", border: "#6366f1", text: "#4f46e5" },
  neutral: { bg: "rgba(148,163,184,0.08)", border: "#94a3b8", text: "#64748b" },
};

/* ──────────────────────────────────────────────────────────── */
/*                TAB definitions                              */
/* ──────────────────────────────────────────────────────────── */
const TABS = [
  { key: "insights", label: "Insights", icon: FaLightbulb },
  { key: "comparison", label: "Comparison", icon: FaExchangeAlt },
  { key: "share", label: "Share Analysis", icon: FaChartPie },
];

/* ──────────────────────────────────────────────────────────── */
/*              SecurityAnalyticsModal component                */
/* ──────────────────────────────────────────────────────────── */

/**
 * Props:
 *   open       (bool)   — whether modal is visible
 *   onClose    (func)   — close handler
 *   data       (object) — the full security overview data (needs data.monthlyEndpointTrend)
 *   clientName (string) — selected client name for the subtitle
 *   isMobile   (bool)   — responsive flag
 */
const SecurityAnalyticsModal = ({ open, onClose, data, clientName, isMobile }) => {
  const [activeTab, setActiveTab] = useState("insights");
  const [compMonthA, setCompMonthA] = useState(null);
  const [compMonthB, setCompMonthB] = useState(null);
  const [shareMonth, setShareMonth] = useState(null);

  return (
    <ReusableModal
      open={open}
      onClose={onClose}
      title="Security Analytics"
      subtitle={`Deep-dive insights for ${clientName || "selected client"}`}
      icon={FaChartBar}
      iconColor="#6366f1"
      iconBg="rgba(99,102,241,0.15)"
      width="min(95vw, 850px)"
      maxHeight="85vh"
    >
      {/* ──── Tab Bar ──── */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid var(--color-border)",
          background: "var(--color-bg-card)",
          flexShrink: 0,
        }}
      >
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              flex: 1,
              padding: "12px 0",
              background: "transparent",
              border: "none",
              borderBottom:
                activeTab === tab.key
                  ? "2px solid #6366f1"
                  : "2px solid transparent",
              color:
                activeTab === tab.key ? "#6366f1" : "var(--color-text-light)",
              fontWeight: activeTab === tab.key ? 600 : 400,
              fontSize: 13,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab.key)
                e.currentTarget.style.color = "#6366f1";
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab.key)
                e.currentTarget.style.color = "var(--color-text-light)";
            }}
          >
            <tab.icon size={13} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ──── Scrollable Content ──── */}
      <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? 16 : 24 }}>

        {/* ===== INSIGHTS TAB ===== */}
        {activeTab === "insights" && (() => {
          const insights = computeInsights(data?.monthlyEndpointTrend);

          return (
            <div>
              <div className="d-flex align-items-center gap-2 mb-3">
                <HeadingIcon icon={FaLightbulb} color="#f59e0b" bg="rgba(245,158,11,0.15)" />
                <div>
                  <h6 className="fw-bold mb-0" style={{ color: "var(--color-text)", fontSize: 14 }}>
                    Auto-Generated Insights
                  </h6>
                  <span style={{ fontSize: 11, color: "var(--color-text-light)" }}>
                    Based on your monthly endpoint trend data
                  </span>
                </div>
              </div>

              {insights.length === 0 ? (
                <div style={{ padding: 40, textAlign: "center", color: "var(--color-text-light)", fontSize: 13 }}>
                  Not enough trend data to generate insights.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {insights.map((insight, idx) => {
                    const colors = INSIGHT_COLORS[insight.type] || INSIGHT_COLORS.neutral;
                    const InsightIcon = insight.icon;
                    return (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.08 }}
                        style={{
                          padding: "14px 16px",
                          borderRadius: 12,
                          background: colors.bg,
                          borderLeft: `4px solid ${colors.border}`,
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 12,
                        }}
                      >
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            background: colors.border + "20",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <InsightIcon size={16} color={insight.iconColor} />
                        </div>
                        <span style={{ fontSize: 13, color: "var(--color-text)", lineHeight: 1.5, fontWeight: 500, paddingTop: 6 }}>
                          {insight.text}
                        </span>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        {/* ===== COMPARISON TAB ===== */}
        {activeTab === "comparison" && (() => {
          const trend = data?.monthlyEndpointTrend;
          const labels = trend?.labels || [];
          const effectiveA = compMonthA != null ? compMonthA : Math.max(0, labels.length - 2);
          const effectiveB = compMonthB != null ? compMonthB : labels.length - 1;
          const comparison = computeComparison(trend, effectiveA, effectiveB);

          const butterflyData = {
            labels: comparison.map((c) => c.tool),
            datasets: [
              {
                label: labels[effectiveA] || "Month A",
                data: comparison.map((c) => -c.valueA),
                backgroundColor: comparison.map((c) => c.color + "88"),
                borderColor: comparison.map((c) => c.color),
                borderWidth: 1,
                borderRadius: 4,
              },
              {
                label: labels[effectiveB] || "Month B",
                data: comparison.map((c) => c.valueB),
                backgroundColor: comparison.map((c) => c.color),
                borderColor: comparison.map((c) => c.color),
                borderWidth: 1,
                borderRadius: 4,
              },
            ],
          };

          return (
            <div>
              <div className="d-flex align-items-center gap-2 mb-3">
                <HeadingIcon icon={FaExchangeAlt} color="#3b82f6" bg="rgba(59,130,246,0.15)" />
                <div>
                  <h6 className="fw-bold mb-0" style={{ color: "var(--color-text)", fontSize: 14 }}>
                    Period-over-Period Comparison
                  </h6>
                  <span style={{ fontSize: 11, color: "var(--color-text-light)" }}>
                    Compare any two months side by side
                  </span>
                </div>
              </div>

              {/* Month Selectors */}
              <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 140 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-light)", marginBottom: 4, display: "block" }}>
                    Period A
                  </label>
                  <select
                    value={effectiveA}
                    onChange={(e) => setCompMonthA(parseInt(e.target.value))}
                    style={{
                      width: "100%",
                      borderRadius: 8,
                      border: "1px solid var(--color-border)",
                      background: "var(--color-bg-card)",
                      color: "var(--color-text)",
                      padding: "8px 12px",
                      fontSize: 12,
                      cursor: "pointer",
                      outline: "none",
                    }}
                  >
                    {labels.map((label, i) => (
                      <option key={i} value={i}>{label}</option>
                    ))}
                  </select>
                </div>
                <div style={{
                  display: "flex",
                  alignItems: "flex-end",
                  paddingBottom: 8,
                  color: "var(--color-text-light)",
                  fontSize: 13,
                  fontWeight: 600,
                }}>
                  vs
                </div>
                <div style={{ flex: 1, minWidth: 140 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-light)", marginBottom: 4, display: "block" }}>
                    Period B
                  </label>
                  <select
                    value={effectiveB}
                    onChange={(e) => setCompMonthB(parseInt(e.target.value))}
                    style={{
                      width: "100%",
                      borderRadius: 8,
                      border: "1px solid var(--color-border)",
                      background: "var(--color-bg-card)",
                      color: "var(--color-text)",
                      padding: "8px 12px",
                      fontSize: 12,
                      cursor: "pointer",
                      outline: "none",
                    }}
                  >
                    {labels.map((label, i) => (
                      <option key={i} value={i}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Butterfly Chart */}
              {comparison.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ height: Math.max(250, comparison.length * 50) }}>
                    <Bar
                      data={butterflyData}
                      options={{
                        indexAxis: "y",
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: true,
                            position: "top",
                            labels: { color: "var(--color-text)", font: { size: 11 }, boxWidth: 12, padding: 15 },
                          },
                          tooltip: {
                            backgroundColor: "var(--color-bg-card)",
                            titleColor: "var(--color-text)",
                            bodyColor: "var(--color-text)",
                            borderColor: "var(--color-border)",
                            borderWidth: 1,
                            callbacks: {
                              label: (ctx) => ` ${ctx.dataset.label}: ${Math.abs(ctx.parsed.x)}`,
                            },
                          },
                        },
                        scales: {
                          x: {
                            ticks: {
                              callback: (v) => Math.abs(v),
                              color: "var(--color-text-light)",
                              font: { size: 10 },
                            },
                            grid: { color: "rgba(148,163,184,0.1)" },
                          },
                          y: {
                            ticks: { color: "var(--color-text)", font: { size: 11 } },
                            grid: { display: false },
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Comparison Table */}
              {comparison.length > 0 && (
                <div style={{ borderRadius: 12, border: "1px solid var(--color-border)", overflow: "hidden" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: "rgba(99,102,241,0.06)" }}>
                        <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, color: "var(--color-text)", borderBottom: "1px solid var(--color-border)" }}>Tool</th>
                        <th style={{ padding: "10px 14px", textAlign: "center", fontWeight: 600, color: "var(--color-text)", borderBottom: "1px solid var(--color-border)" }}>{labels[effectiveA]}</th>
                        <th style={{ padding: "10px 14px", textAlign: "center", fontWeight: 600, color: "var(--color-text)", borderBottom: "1px solid var(--color-border)" }}>{labels[effectiveB]}</th>
                        <th style={{ padding: "10px 14px", textAlign: "center", fontWeight: 600, color: "var(--color-text)", borderBottom: "1px solid var(--color-border)" }}>Change</th>
                        <th style={{ padding: "10px 14px", textAlign: "center", fontWeight: 600, color: "var(--color-text)", borderBottom: "1px solid var(--color-border)" }}>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.map((row, idx) => (
                        <tr
                          key={row.tool}
                          style={{
                            borderBottom: idx < comparison.length - 1 ? "1px solid var(--color-border)" : "none",
                            transition: "background 0.15s ease",
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(99,102,241,0.04)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                        >
                          <td style={{ padding: "10px 14px", color: "var(--color-text)" }}>
                            <div className="d-flex align-items-center gap-2">
                              <span style={{ width: 8, height: 8, borderRadius: "50%", background: row.color, flexShrink: 0 }} />
                              {row.tool}
                            </div>
                          </td>
                          <td style={{ padding: "10px 14px", textAlign: "center", color: "var(--color-text)" }}>{row.valueA}</td>
                          <td style={{ padding: "10px 14px", textAlign: "center", color: "var(--color-text)", fontWeight: 600 }}>{row.valueB}</td>
                          <td style={{ padding: "10px 14px", textAlign: "center" }}>
                            <span style={{
                              color: row.delta > 0 ? "#22c55e" : row.delta < 0 ? "#ef4444" : "var(--color-text-light)",
                              fontWeight: 600,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                            }}>
                              {row.delta > 0 && <FaArrowUp size={9} />}
                              {row.delta < 0 && <FaArrowDown size={9} />}
                              {row.delta > 0 ? "+" : ""}{row.delta}
                            </span>
                          </td>
                          <td style={{ padding: "10px 14px", textAlign: "center" }}>
                            <span style={{
                              padding: "2px 8px",
                              borderRadius: 6,
                              fontSize: 11,
                              fontWeight: 600,
                              background: row.delta > 0 ? "rgba(34,197,94,0.1)" : row.delta < 0 ? "rgba(239,68,68,0.1)" : "rgba(148,163,184,0.1)",
                              color: row.delta > 0 ? "#22c55e" : row.delta < 0 ? "#ef4444" : "var(--color-text-light)",
                            }}>
                              {row.pctChange === "NEW" ? "NEW" : (row.delta > 0 ? "+" : "") + row.pctChange + "%"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {comparison.length === 0 && (
                <div style={{ padding: 40, textAlign: "center", color: "var(--color-text-light)", fontSize: 13 }}>
                  No trend data available for comparison.
                </div>
              )}
            </div>
          );
        })()}

        {/* ===== SHARE ANALYSIS TAB ===== */}
        {activeTab === "share" && (() => {
          const trend = data?.monthlyEndpointTrend;
          const labels = trend?.labels || [];
          const lastIdx = labels.length - 1;
          const effectiveShareMonth = shareMonth != null ? shareMonth : lastIdx;
          const shareData = computeShare(trend, effectiveShareMonth);
          const totalValue = shareData.reduce((sum, s) => sum + s.value, 0);

          return (
            <div>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center gap-2">
                  <HeadingIcon icon={FaChartPie} color="#8b5cf6" bg="rgba(139,92,246,0.15)" />
                  <div>
                    <h6 className="fw-bold mb-0" style={{ color: "var(--color-text)", fontSize: 14 }}>
                      Proportional Share
                    </h6>
                    <span style={{ fontSize: 11, color: "var(--color-text-light)" }}>
                      Each tool's share of total endpoints ({totalValue.toLocaleString()} total)
                    </span>
                  </div>
                </div>

                <select
                  value={effectiveShareMonth}
                  onChange={(e) => setShareMonth(parseInt(e.target.value))}
                  style={{
                    borderRadius: 8,
                    border: "1px solid var(--color-border)",
                    background: "var(--color-bg-card)",
                    color: "var(--color-text)",
                    padding: "6px 12px",
                    fontSize: 12,
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  {labels.map((label, i) => (
                    <option key={i} value={i}>{label}</option>
                  ))}
                </select>
              </div>

              {shareData.length === 0 ? (
                <div style={{ padding: 40, textAlign: "center", color: "var(--color-text-light)", fontSize: 13 }}>
                  No data available for this month.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {shareData.map((item, idx) => (
                    <motion.div
                      key={item.tool}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.06 }}
                    >
                      {/* Tool name + value row */}
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <div className="d-flex align-items-center gap-2">
                          <span style={{ width: 10, height: 10, borderRadius: "50%", background: item.color, flexShrink: 0 }} />
                          <span style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text)" }}>{item.tool}</span>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                          {/* Mini sparkline */}
                          <div style={{ width: 60, height: 22 }}>
                            <Line
                              data={{
                                labels: item.sparkData.map((_, i) => i),
                                datasets: [{
                                  data: item.sparkData,
                                  borderColor: item.color,
                                  backgroundColor: item.color + "33",
                                  fill: true,
                                  tension: 0.4,
                                  pointRadius: 0,
                                  borderWidth: 1.5,
                                }],
                              }}
                              options={{
                                maintainAspectRatio: false,
                                plugins: { legend: { display: false }, tooltip: { enabled: false } },
                                scales: { x: { display: false }, y: { display: false } },
                                elements: { point: { radius: 0 } },
                              }}
                            />
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text)", minWidth: 50, textAlign: "right" }}>
                            {item.value.toLocaleString()}
                          </span>
                        </div>
                      </div>

                      {/* Progress bar */}
                      <div style={{
                        width: "100%",
                        height: 22,
                        borderRadius: 6,
                        background: (() => {
                          const isDark = document.body.classList.contains("dark-mode");
                          return isDark ? "rgba(148,163,184,0.12)" : "rgba(148,163,184,0.15)";
                        })(),
                        overflow: "hidden",
                        position: "relative",
                      }}>
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${item.share}%` }}
                          transition={{ duration: 0.8, delay: idx * 0.06, ease: "easeOut" }}
                          style={{
                            height: "100%",
                            borderRadius: 6,
                            background: `linear-gradient(90deg, ${item.color}CC, ${item.color})`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            paddingRight: 8,
                            minWidth: item.share > 5 ? "auto" : 0,
                          }}
                        >
                          {item.share > 8 && (
                            <span style={{ fontSize: 10, fontWeight: 700, color: "#fff" }}>
                              {item.share}%
                            </span>
                          )}
                        </motion.div>
                        {item.share <= 8 && (
                          <span style={{
                            position: "absolute",
                            left: `calc(${item.share}% + 6px)`,
                            top: "50%",
                            transform: "translateY(-50%)",
                            fontSize: 10,
                            fontWeight: 700,
                            color: "var(--color-text-light)",
                          }}>
                            {item.share}%
                          </span>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

      </div>
    </ReusableModal>
  );
};

export default SecurityAnalyticsModal;
